import React from 'react';
import { Mail, Phone } from 'lucide-react';
import founderPortrait from '@/assets/founder-portrait.jpg';

const FounderProfile = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden bg-primary">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Omollo Victor
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto mb-8">
              LLB Hons | Certified Professional Mediator | Trainee Advocate | Low-Code Developer | Student Member CIArb | Certified Secretary (Advanced)
            </p>
            <div className="max-w-6xl mx-auto text-lg text-blue-100 space-y-4 text-justify px-4">
              <p>
                Omollo Victor is a dedicated and innovative legal professional with strong expertise in Alternative Dispute Resolution, Commercial Law, Intellectual Property, Cyber Law, AI Law, and Corporate Governance. Young and fully in touch with emerging issues, he is committed to advancing modern legal practice through thoughtful, future-focused solutions.
              </p>
              <p>
                Beyond his legal work, Victor is also a low-code developer with experience in UI and UX design. As the Founder and CEO of Wakili Assistant, he leverages technology to build impactful legal-tech tools and automation systems that enhance efficiency and access to legal services. Driven, adaptable, and ready to take up any responsibility, he brings both energy and clarity to every project he undertakes.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        {/* Professional Image and Contact */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
          <div className="lg:col-span-1">
            <div className="flex flex-col items-center">
              <div className="relative overflow-hidden rounded-lg shadow-lg w-full max-w-sm mb-6">
                <img 
                  src={founderPortrait}
                  alt="Omollo Victor - Professional Profile"
                  className="w-full h-auto object-cover transition-all duration-700 hover:scale-110 hover:brightness-110"
                />
              </div>
              <div className="bg-gray-50 p-6 rounded-lg w-full max-w-sm">
                <h3 className="text-xl font-bold text-primary mb-4 text-center">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Mail className="text-secondary" size={20} />
                    <span className="text-gray-700">info@lawandcode.africa</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="text-secondary" size={20} />
                    <span className="text-gray-700">+254731797354</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-2">
            <h2 className="text-3xl font-bold text-primary mb-8">Background and Qualifications</h2>
            <div className="space-y-6 text-gray-700">
              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Educational Foundation</h3>
                <div className="space-y-3">
                  <p>
                    <strong>Kanga National School:</strong> Attended Kanga National School, where he held the position of Secretary General of the Student Governing Council, demonstrating early leadership capabilities.
                  </p>
                  <p>
                    <strong>Kisii University (LLB):</strong> Obtained an LLB from Kisii University. During his tenure, contributed to legal and governance frameworks as a member of the Kisii University Students Association (KSUSA) Constitution Review Committee.
                  </p>
                  <p>
                    <strong>Kenya School of Law:</strong> Currently an ATP Student at the Kenya School of Law.
                  </p>
                </div>
              </div>

              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Professional Qualifications</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Certified Professional Mediator</h4>
                    <p className="text-sm text-gray-600">Holds certification as a Professional Mediator</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Trainee Advocate</h4>
                    <p className="text-sm text-gray-600">Currently undergoing training as an Advocate</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Student Member CIArb</h4>
                    <p className="text-sm text-gray-600">Student member of the Chartered Institute of Arbitrators</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Low-Code/No-Code Developer</h4>
                    <p className="text-sm text-gray-600">Proficient in low-code/no-code development</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Certified Secretary Course</h4>
                    <p className="text-sm text-gray-600">Certified Secretary Course, Advanced Level</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Professional Experience & Community Involvement</h3>
                <div className="space-y-3">
                  <p>
                    <strong>Ogwe and Associates Advocates:</strong> Completed internship gaining practical legal experience in various practice areas.
                  </p>
                  <p>
                    <strong>Baraka CBO:</strong> Founding member of Baraka Community Based Organization based in Kisii, offering "comrade support" to deserving students.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Areas of Specialization */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary mb-8 text-center">
            Key Practice Areas and Interests
          </h2>
          <p className="text-lg text-gray-600 text-center mb-10 max-w-4xl mx-auto">
            Omollo Victor's professional interests and developing specializations encompass:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Alternative Dispute Resolution (ADR)</h3>
              <p className="text-gray-600">
                Focus on mediation, arbitration, and non-litigious dispute resolution mechanisms across various sectors including technology and commercial disputes.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Commercial Law</h3>
              <p className="text-gray-600">
                Corporate governance frameworks, commercial contracts negotiation and drafting, international trade law compliance, and business regulatory matters.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Intellectual Property (IP) Law</h3>
              <p className="text-gray-600">
                Protection of innovations, patent applications, trademark registration, copyright matters, and IP licensing in technology sectors.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Cyber Law</h3>
              <p className="text-gray-600">
                Data protection compliance, cybersecurity regulations, digital privacy rights, and legal frameworks for digital transactions.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">AI Law</h3>
              <p className="text-gray-600">
                AI governance frameworks, algorithmic accountability, machine learning compliance, and regulatory implications of artificial intelligence deployment.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Professional Attributes</h3>
              <p className="text-gray-600">
                Analytical thinking, adaptable problem-solving, solution-oriented approach, innovative mindset, and demonstrated leadership capabilities.
              </p>
            </div>
          </div>
        </div>

        {/* Professional Outlook */}
        <div className="bg-gradient-to-r from-primary to-primary/90 text-white p-8 rounded-lg">
          <h2 className="text-3xl font-bold mb-6 text-center">Professional Trajectory</h2>
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-blue-100 leading-relaxed">
              Victor is committed to continuous professional development within the legal sector, 
              particularly at the intersection of law and technology. The objective is to apply legal acumen 
              and technical skills to address contemporary legal challenges and contribute to evolving legal domains.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:info@lawandcode.africa"
                className="bg-secondary hover:bg-secondary/90 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 inline-flex items-center gap-2"
              >
                <Mail size={20} />
                Connect via Email
              </a>
              <a 
                href="https://wa.me/254731797354"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/20 hover:bg-white/30 text-white border border-white/30 px-6 py-3 rounded-full font-semibold transition-all duration-300 inline-flex items-center gap-2"
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.525 3.488"/>
                </svg>
                Connect via WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FounderProfile;
